import type { ReactElement } from "react";

import useSourcelabForm, { renderFormElement } from "./form-renderer";
import type { FormProps } from "./types";
import { isLayout } from "./types";
import type {
  Resolver,
  SubmitErrorHandler,
  SubmitHandler,
} from "react-hook-form";

import * as DefaultComponents from "./components/default";
import { BRAND_COMPONENT_OVERRIDES } from "./components";

// Type for the field - using a generic approach
type AlphaField = {
  name: string;
  type?: string;
  customControlType?: string;
  uiOptions?: {
    visible?: (formValues: Record<string, any>) => boolean;
    label?: string;
    conditionalLabel?: (formValues: Record<string, any>) => string;
    labelClassName?: string;
    className?: string;
  };
  validations?: string[];
  [key: string]: any;
};

// Map customControlType to component name
function getComponentForControlType(
  customControlType: string,
  brand: string
): any {
  const mapping: Record<string, string> = {
    email: "CustomEmail",
    dateInput: "CustomDate",
    checkbox: "CustomCheckbox",
    text: "CustomInput",
    radioGroupGender: "CustomRadioGender",
    "shadcn-select": "CustomShadcnSelect",
    switch: "CustomSwitch",
    textarea: "CustomTextarea",
    radioGroup: "CustomRadioGroup",
    "picture-choice": "CustomPictureChoice",
    radioGroupHappiness: "CustomRadioHappiness",
  };

  const componentName = mapping[customControlType];
  if (!componentName) return null;

  const overrides = BRAND_COMPONENT_OVERRIDES[brand] as
    | Record<string, unknown>
    | undefined;
  return (
    overrides?.[componentName] ??
    (DefaultComponents as Record<string, unknown>)[componentName] ??
    null
  );
}

/**
 * End-User Form Renderer with Brand Support
 *
 * This component renders forms for end-users with brand-specific component variations.
 */
export default function EndUserForm<D extends Record<string, unknown>>({
  fields,
  brand = "default",
  onSubmit,
  resolver,
  className,
  formClassName,
  values,
  onInvalidForm,
  onFieldChange,
  translateError,
}: {
  fields: any[]; // Accept any field type for flexibility
  brand?: string;
  onSubmit?: SubmitHandler<D>;
  resolver?: Resolver<D, any>;
  className?: string;
  formClassName?: string;
  values?: any;
  onInvalidForm?: SubmitErrorHandler<D>;
  onFieldChange?: any;
  translateError?: (errorKey: string) => string;
}) {
  const renderBrandAwareField = (
    field: AlphaField,
    formProps: FormProps
  ): ReactElement | undefined => {
    if (isLayout(field)) return undefined;

    if (
      field.uiOptions?.visible &&
      !field.uiOptions?.visible(formProps.formValues)
    )
      return undefined;

    // Handle custom types with brand-aware components
    if (field.type === "custom" && field.customControlType) {
      const Component = getComponentForControlType(
        field.customControlType,
        brand
      );

      if (Component) {
        // Compose with label and error handling
        const errorKey = formProps.errors[field.name]?.message as
          | string
          | undefined;

        // Translate error message if translator is provided, otherwise show key
        const errorMessage = errorKey
          ? translateError
            ? translateError(errorKey)
            : errorKey
          : undefined;

        // Check if field is required - support both old and new validation formats
        const isRequired =
          field.validations?.some((v) => v === "required") ||
          Boolean(field.validation?.required);

        return (
          <div key={field.name} className="flex flex-col gap-1 py-2 w-full text-slate-50">
            {field.uiOptions?.label && (
              <label
                className={field.uiOptions.labelClassName}
                htmlFor={field.name}
              >
                {field.uiOptions.label}
                {isRequired && (
                  <span className="ml-1 align-top text-destructive text-sm">*</span>
                )}
              </label>
            )}
            {field.uiOptions?.conditionalLabel && (
              <label htmlFor={field.name}>
                {field.uiOptions.conditionalLabel(formProps.formValues)}
              </label>
            )}
            <span className={field.uiOptions?.className}>
              <Component field={field} formProps={formProps} />
            </span>
            {errorMessage && (
              <span
                data-testid={`${field.name}-error`}
                className="text-destructive text-sm px-6"
              >
                {errorMessage}
              </span>
            )}
          </div>
        );
      }
    }

    // Fallback to standard form element rendering
    return renderFormElement(field as any, formProps);
  };

  const customCompose = (field: any, formProps: FormProps) => {
    return renderBrandAwareField(field as AlphaField, formProps);
  };

  const { render, onFormSubmit } = useSourcelabForm({
    values: values as D,
    onSubmit,
    onFieldChange,
    customRender: customCompose,
    onInvalidForm,
    mode: "onChange",
    resetAfterSubmit: true,
    nestedClassName: "",
    resolver,
  });

  return (
    <div className={className}>
      <form onSubmit={onFormSubmit} className={formClassName}>
        {render(fields as any)}
      </form>
    </div>
  );
}
